import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchOtherStoreOrder } from "../api";
import { copyTextToClipboard } from "../helpers";
import { isTerminalOrder, isUnpaidFinal, isWaitingForFunds } from "../order-status";
import {
  dropOrderFromUrl,
  forgetRecentStoreOrder,
  getRecentStoreOrder,
  getRecentStoreOrderId,
  rememberOrderToken,
  saveRecentStoreOrder,
} from "../order-storage";
import OtherStoreSupportLinks from "./OtherStoreSupportLinks";
import RedeemCodesLink from "./RedeemCodesLink";

function OtherStoreRecentCodes({
  storeId,
  orderId: orderIdProp,
  accessToken: accessTokenProp,
}) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(Boolean(orderIdProp));
  const [copiedKey, setCopiedKey] = useState("");

  const storedOrder = getRecentStoreOrder(storeId);
  const orderId = orderIdProp || storedOrder?.orderId || getRecentStoreOrderId(storeId);
  const accessToken =
    accessTokenProp || storedOrder?.accessToken || null;

  useEffect(() => {
    if (orderIdProp && storeId) {
      saveRecentStoreOrder(storeId, orderIdProp, accessTokenProp);
    }
  }, [orderIdProp, storeId, accessTokenProp]);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let timerId;

    const load = async () => {
      try {
        const data = await fetchOtherStoreOrder(orderId, { accessToken });
        if (!cancelled) {
          setOrder(data);
          if (data?.storeId && data.storeId !== storeId) {
            setOrder(null);
          }
        }
        return data;
      } catch {
        if (!cancelled) {
          setOrder(null);
        }
        return null;
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const poll = async () => {
      const data = await load();
      if (cancelled) {
        return;
      }
      if (!isTerminalOrder(data)) {
        timerId = window.setTimeout(poll, 4000);
      }
    };

    setLoading(true);
    poll();

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [orderId, storeId, accessToken]);

  // Неоплаченный заказ показываем один раз: после обновления страницы блок снова пустой
  useEffect(() => {
    if (!isUnpaidFinal(order) || !orderId) {
      return;
    }
    forgetRecentStoreOrder(storeId, orderId);
    dropOrderFromUrl(orderId);
  }, [order, orderId, storeId]);

  if (!orderId) {
    return (
      <section className="other-store-codes-panel" aria-live="polite">
        <h2 className="other-store-codes-title">Ваши коды</h2>
        <p className="other-store-codes-status">
          Здесь появятся ваши коды, если после успешной оплаты нажать
          «Вернуться в магазин».
        </p>
      </section>
    );
  }

  const isPaid = order?.paymentStatus === "SUCCESS";
  const isFulfilled = order?.fulfillmentStatus === "SUCCESS";
  const isCancelled = order?.paymentStatus === "CANCELLED";
  const isFailed =
    order?.paymentStatus === "FAILED" || order?.fulfillmentStatus === "FAILED";
  const waitingFunds = isWaitingForFunds(order);
  const hasKeys = isFulfilled && order?.keys?.length > 0;
  const needsAuth = Boolean(order?.requiresAuth);

  const handleCopy = async (key) => {
    const copied = await copyTextToClipboard(key);
    if (copied) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(""), 2000);
    }
  };

  return (
    <section className="other-store-codes-panel" aria-live="polite">
      <h2 className="other-store-codes-title">Ваши коды</h2>

      {loading && !order ? (
        <p className="other-store-codes-status">Загрузка заказа…</p>
      ) : null}

      {!loading && !order ? (
        <p className="other-store-codes-status">Заказ не найден</p>
      ) : null}

      {order && needsAuth && isPaid ? (
        <p className="other-store-codes-status">
          Коды отправлены в письме на {order.email}. На сайте они видны только
          по персональной ссылке после оплаты.
        </p>
      ) : null}

      {order && (isFailed || isCancelled) ? (
        <p className="other-store-error">
          {order.paymentError ||
            order.fulfillmentError ||
            (isCancelled ? "Оплата отменена." : "Оплата не прошла.")}
        </p>
      ) : null}

      {order && !isPaid && !isFailed && !isCancelled ? (
        <p className="other-store-codes-status">Ожидаем подтверждение оплаты…</p>
      ) : null}

      {waitingFunds ? (
        <div className="other-store-delay-notice">
          <p className="other-store-codes-status">
            {order.fulfillmentError ||
              "Ваш код задерживается. Можете написать в техподдержку для уточнения сроков."}
          </p>
          <OtherStoreSupportLinks support={order.support} />
        </div>
      ) : null}

      {order && isPaid && !isFulfilled && !isFailed && !waitingFunds ? (
        <p className="other-store-codes-status">
          Оплата получена. Получаем код из магазина…
        </p>
      ) : null}

      {hasKeys ? (
        <>
          <ul className="other-store-keys-list other-store-keys-list--compact">
            {order.keys.map((item) => (
              <li key={item.id || item.key} className="other-store-key-item">
                <code>{item.key}</code>
                <button
                  type="button"
                  className="other-store-key-copy"
                  onClick={() => handleCopy(item.key)}
                >
                  {copiedKey === item.key ? "Скопировано" : "Скопировать"}
                </button>
                {item.productName ? (
                  <span className="other-store-key-meta">{item.productName}</span>
                ) : null}
              </li>
            ))}
          </ul>
          <RedeemCodesLink storeId={storeId} count={order.keys.length} />
          <p className="other-store-codes-note">
            Код также отправлен на {order.email}.
          </p>
        </>
      ) : null}

      {order ? (
        <Link
          to={`/store/order/${order.orderId}`}
          onClick={() => rememberOrderToken(order.orderId, accessToken)}
          className="other-store-codes-link"
        >
          Подробнее о заказе
        </Link>
      ) : null}
    </section>
  );
}

export default OtherStoreRecentCodes;
